import { type FC, use, useCallback, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import { cn } from "@heroui/react";
import { Utils } from "@binale-tech/shared";
import { TriangleExclamation } from "@gravity-ui/icons";
import dayjs from "dayjs";

import { CompanyContext } from "@domain/context/CompanyContext";
import { UserContext } from "@domain/context/UserProvider";
import { ProductAccessUtils } from "@domain/models/utils/ProductAccessUtils";
import { ProductKeys } from "@domain/models/Product";
import { useOpenPositionEvents } from "@accounting/common/calendar/useOpenPositionEvents";
import type {
    OpenPositionBucket,
    OpenPositionBucketKey,
    OpenPositionSide,
} from "@accounting/common/calendar/openPositions";
import { AppRoutes, getRoute, ModuleSubGroups } from "domain/routing/routeConstants";
import { HomeWidgetShell } from "@app/pages/home/HomeWidgetShell";
import { HomeExpandToggle } from "@app/pages/home/HomeExpandToggle";

type Side = "debitor" | "kreditor";
const SIDES: Side[] = ["debitor", "kreditor"];

const BUCKET_LABEL_ID: Record<OpenPositionBucketKey, string> = {
    ueberfaellig: "app.home.openPositions.bucket.ueberfaellig",
    heute: "app.home.openPositions.bucket.heute",
    woche: "app.home.openPositions.bucket.woche",
    spaeter: "app.home.openPositions.bucket.spaeter",
};

const SIDE_LABEL_ID: Record<Side, string> = {
    debitor: "app.home.openPositions.debitor",
    kreditor: "app.home.openPositions.kreditor",
};

const SIDE_DOT_CLASS: Record<Side, string> = {
    debitor: "bg-sky-500",
    kreditor: "bg-indigo-500",
};

const SideDot: FC<{ side: Side; overdue: boolean }> = ({ side, overdue }) => (
    <span className="flex items-center gap-1">
        <span className={cn("size-2 rounded-full", overdue ? "bg-red-500" : SIDE_DOT_CLASS[side])} />
        {overdue && <TriangleExclamation className="size-3.5 text-red-500" />}
    </span>
);

const Skeleton: FC = () => (
    <div
        data-testid="home-open-positions-skeleton"
        className="HomeOpenPositionsWidget w-120 rounded-2xl bg-app-surface/50 backdrop-blur-md border border-app-surface/80 shadow-lg p-4"
    >
        <div className="animate-pulse space-y-3">
            <div className="h-3 w-32 rounded bg-app-text/20" />
            <div className="h-3 w-full rounded bg-app-text/10" />
            <div className="h-3 w-full rounded bg-app-text/10" />
            <div className="h-3 w-2/3 rounded bg-app-text/10" />
        </div>
    </div>
);

export const HomeOpenPositionsWidget: FC = () => {
    const { companyGQL } = use(CompanyContext);
    const user = use(UserContext);
    const { status, buckets } = useOpenPositionEvents();

    const [expanded, setExpanded] = useState(false);
    const toggle = useCallback(() => setExpanded((v) => !v), []);

    if (!ProductAccessUtils.canRead(ProductKeys.AccountingCommon, companyGQL, user)) {
        return null;
    }
    if (status !== "ready") {
        return <Skeleton />;
    }

    const nonEmptyBuckets: OpenPositionBucket[] = buckets.filter((b) => b.debitor.count + b.kreditor.count > 0);
    const isEmpty = nonEmptyBuckets.length === 0;
    const bucketSideRows: { bucket: OpenPositionBucket; side: Side; data: OpenPositionSide }[] =
        nonEmptyBuckets.flatMap((bucket) =>
            SIDES.filter((side) => bucket[side].count > 0).map((side) => ({ bucket, side, data: bucket[side] })),
        );

    const header = (
        <div className="flex w-full items-center justify-between gap-4">
            <span className="text-sm font-bold uppercase tracking-wide opacity-70">
                <FormattedMessage id="app.home.openPositions.title" />
            </span>
            {!isEmpty && <HomeExpandToggle expanded={expanded} onToggle={toggle} />}
        </div>
    );

    const collapsedBody = isEmpty ? (
        <div className="mt-3 text-sm opacity-60">
            <FormattedMessage id="app.home.openPositions.empty" />
        </div>
    ) : (
        <div className="mt-3 space-y-2">
            {bucketSideRows.map(({ bucket, side, data }) => (
                <div key={`${bucket.key}-${side}`} className="flex items-center justify-between gap-3 text-sm">
                    <span className="flex items-center gap-2 opacity-80">
                        <SideDot side={side} overdue={bucket.key === "ueberfaellig"} />
                        <FormattedMessage id={SIDE_LABEL_ID[side]} />{" "}
                        <FormattedMessage id={BUCKET_LABEL_ID[bucket.key]} /> ({data.count})
                    </span>
                    <span className="font-mono text-sm font-semibold tabular-nums">
                        {Utils.CurrencyUtils.currencyFormat(data.sumCents)}
                    </span>
                </div>
            ))}
        </div>
    );

    const expandedBody = (
        <div className="mt-4 space-y-4">
            {nonEmptyBuckets.map((bucket) => (
                <section key={bucket.key}>
                    <div className="text-xs font-bold uppercase opacity-60">
                        <FormattedMessage id={BUCKET_LABEL_ID[bucket.key]} />
                    </div>
                    <div className="mt-1 space-y-3">
                        {SIDES.filter((side) => bucket[side].count > 0).map((side) => (
                            <div key={side}>
                                <div className="flex items-center justify-between gap-3">
                                    <Link
                                        to={
                                            side === "kreditor"
                                                ? getRoute(AppRoutes.invoiceAnalysisOpos, {
                                                      subGroup: ModuleSubGroups.ER,
                                                  })
                                                : AppRoutes.debitorAnalysisOpos
                                        }
                                        className="flex items-center gap-2 text-xs font-semibold opacity-70"
                                    >
                                        <SideDot side={side} overdue={bucket.key === "ueberfaellig"} />
                                        <FormattedMessage id={SIDE_LABEL_ID[side]} /> ({bucket[side].count})
                                    </Link>
                                    <span className="font-mono text-xs font-semibold tabular-nums opacity-80">
                                        {Utils.CurrencyUtils.currencyFormat(bucket[side].sumCents)}
                                    </span>
                                </div>
                                <div className="mt-1 space-y-1">
                                    {bucket[side].items.map((item) => (
                                        <div
                                            key={item.id}
                                            className="flex items-center justify-between gap-4 text-sm text-app-text hover:opacity-80"
                                        >
                                            <span className="truncate opacity-80">{item.contactLabel}</span>
                                            <span className="flex shrink-0 items-baseline gap-3">
                                                <span className="font-mono font-semibold tabular-nums">
                                                    {Utils.CurrencyUtils.currencyFormat(item.amountCents)}
                                                </span>
                                                <span className="w-16 text-right text-xs opacity-60">
                                                    {dayjs(item.date).format("DD.MM.YYYY")}
                                                </span>
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            ))}
        </div>
    );

    return (
        <HomeWidgetShell
            className="HomeOpenPositionsWidget"
            expanded={expanded}
            expandedWidth="w-[640px]"
            header={header}
            collapsed={collapsedBody}
        >
            {expandedBody}
        </HomeWidgetShell>
    );
};
